import { ProductionDecline } from 'types/api-responses';
import type { DcaFormValues } from './decline-curve-analysis/export-dca-to-xlsx';

/** The parts of a bulk-forecast result the parameters sheet reports. */
interface ForecastParams {
  declineStartDate?: string;
  oilDeclineParams?: ProductionDecline | null;
  gasDeclineParams?: ProductionDecline | null;
  waterDeclineParams?: ProductionDecline | null;
}

const EMPTY_STREAM = {
  dlim: null,
  bFactor: null,
  declineModel: '',
  qi: null,
  di: null,
  startDate: null,
  rampupModel: null,
  qiRampup: null,
  rampupDuration: null,
  n: null,
  ri: null,
  tElf: null,
  m: null,
};

/**
 * One stream's decline parameters, in the shape the export writes.
 *
 * @param {ProductionDecline | null | undefined} decline - The stream's parameters.
 * @param {string | undefined} fallbackStartDate - Used when the stream has no start date of its own.
 * @return {object} The stream's row on the parameters sheet.
 */
const streamValues = (
  decline: ProductionDecline | null | undefined,
  fallbackStartDate?: string
) => {
  if (!decline) return EMPTY_STREAM;

  const startDate = decline.declineStartDate ?? fallbackStartDate ?? null;

  return {
    dlim: decline.dLim,
    bFactor: decline.bFactor,
    declineModel: decline.declineModel ?? '',
    qi: decline.qi,
    di: decline.di,
    startDate: startDate === null ? null : new Date(startDate),
    rampupModel: decline.rampUpModel,
    qiRampup: decline.rampUpRate,
    rampupDuration: decline.rampUpMonths,
    n: decline.rampUpSlope,
    ri: decline.ri,
    tElf: decline.tElf,
    m: decline.m,
  };
};

/**
 * The parameters the DCA Forecast table's export reports for a saved forecast.
 *
 * The DCA section reads these off the configuration panel's form, but the Well
 * Analysis section is showing a forecast that was already saved, so they come
 * from the forecast itself.
 *
 * @param {ForecastParams | null} forecast - The selected forecast, or null when none is.
 * @return {DcaFormValues} The parameters sheet's contents.
 */
export const forecastExportValues = (
  forecast: ForecastParams | null
): DcaFormValues => ({
  // The saved forecast reports its parameters already converted, so the export
  // has no format or duration of its own to name.
  diFormat: 'effective',
  qiFormat: 'days',
  forecastDuration: null,
  forecastDurationOption: '',
  oil: streamValues(forecast?.oilDeclineParams, forecast?.declineStartDate),
  gas: streamValues(forecast?.gasDeclineParams, forecast?.declineStartDate),
  water: streamValues(forecast?.waterDeclineParams, forecast?.declineStartDate),
});
